import { z } from 'zod';
import { CloudInfoSchema } from './cloud.js';
import { MAX_INSTALL_DIR, checkInstallDir } from './install-dir.js';
export const MachineOsSchema = z.enum(['macos', 'windows', 'linux']);
export const PairingOsSchema = z.enum(['macos', 'windows', 'linux']);
/** A cloud machine's last self-stop (doc 52). */
export const MachineStopSchema = z.object({ at: z.string(), reason: z.enum(['idle']), idleMinutes: z.number().int().min(0) });
/** What the agent reports about its machine. Untrusted: every field is bounded. */
export const HardwareSchema = z.object({
    osVersion: z.string().max(64),
    arch: z.string().max(32),
    cpu: z.string().max(128),
    ramGb: z.number().min(0).max(4096),
    gpu: z.object({
        kind: z.enum(['mps', 'cuda', 'cpu']),
        name: z.string().max(128),
        vramGb: z.number().min(0).max(1024).nullable(),
        /** Apple GPU core count (agent ≥ 0.7.0; older agents omit it). */
        cores: z.number().int().min(1).max(1024).nullable().optional(),
    }),
    /** A cloud GPU machine (agent ≥ 0.11.0, doc 51); malformed → null, the rest of the hardware stays. */
    cloud: CloudInfoSchema.nullable().optional().catch(null),
});
/** One network card as the agent reports it, for Wake-on-LAN (doc 43). Untrusted: validated. */
export const NetworkCardSchema = z.object({
    mac: z.string().regex(/^[0-9a-f]{2}(:[0-9a-f]{2}){5}$/),
    ipv4: z.ipv4(),
    netmask: z.ipv4(),
});
export const NetworkSchema = z.array(NetworkCardSchema).max(8);
export const MachineNameSchema = z.string().trim().min(1, 'Required').max(64);
export const MachineSchema = z.object({
    id: z.string(),
    name: z.string(),
    os: MachineOsSchema,
    online: z.boolean(),
    createdAt: z.string(),
    lastSeenAt: z.string().nullable(),
    agentVersion: z.string().nullable(),
    comfyuiVersion: z.string().nullable(),
    hardware: HardwareSchema.nullable(),
    /** Offline, and another of the user's machines is online in its network to wake it (doc 43). */
    canWake: z.boolean().default(false),
    /** A pod Couch made (doc 59): Couch can start, stop and delete it. */
    cloudPodId: z.string().nullable().default(null),
    /** When and why a cloud machine last stopped itself (doc 52). */
    lastStop: MachineStopSchema.nullable().default(null),
    /** A home machine's originals go to the archive in the user's bucket (doc 76); cloud machines' always do. */
    archiveOutputs: z.boolean().default(false),
});
export const SetArchiveOutputsRequestSchema = z.object({ on: z.boolean() });
export const WakeResponseSchema = z.object({ helper: z.string() });
/** The agent version Couch serves and the pinned ComfyUI — the cards offer updates to these (doc 11). */
export const LatestVersionsSchema = z.object({ agent: z.string(), comfyui: z.string() });
export const MachinesResponseSchema = z.object({ machines: z.array(MachineSchema), latest: LatestVersionsSchema.optional() });
export const MachineResponseSchema = z.object({ machine: MachineSchema });
export const RenameMachineRequestSchema = z.object({ name: MachineNameSchema });
export const PairingStatusSchema = z.enum(['pending', 'paired', 'expired']);
export const PairingSchema = z.object({
    id: z.string(),
    os: PairingOsSchema,
    expiresAt: z.string(),
    status: PairingStatusSchema,
    machineId: z.string().nullable(),
});
/** Doc 49: an install folder typed on the website, or the folder picker on the machine — not both. */
export const CreatePairingRequestSchema = z.object({
    os: PairingOsSchema,
    installDir: z.string().max(MAX_INSTALL_DIR + 50).optional(),
    pickFolder: z.boolean().optional(),
}).superRefine((body, ctx) => {
    // Doc 50: Linux machines are often servers without a screen — no folder picker there.
    if (body.pickFolder && body.os === 'linux')
        ctx.addIssue({ code: 'custom', path: ['pickFolder'], message: 'Linux has no folder picker — type the folder' });
    const dir = body.installDir?.trim();
    if (!dir)
        return;
    if (body.pickFolder)
        ctx.addIssue({ code: 'custom', path: ['pickFolder'], message: 'Type a folder or pick it on the machine, not both' });
    const check = checkInstallDir(body.os, dir);
    if ('problem' in check)
        ctx.addIssue({ code: 'custom', path: ['installDir'], message: check.problem });
});
/** `command` carries the only copy of the pairing token. */
export const CreatePairingResponseSchema = z.object({ pairing: PairingSchema, command: z.string() });
export const PairingResponseSchema = z.object({ pairing: PairingSchema });
/** Sent by the installer/agent to trade a pairing token for a machine token. */
export const AgentPairRequestSchema = z.object({
    pairingToken: z.string().min(1).max(128),
    name: MachineNameSchema,
    os: MachineOsSchema,
    hardware: HardwareSchema.optional(),
    // nullish: the Python agent sends None (null) for a version it could not read.
    agentVersion: z.string().max(32).nullish().transform((v) => v ?? undefined),
    comfyuiVersion: z.string().max(32).nullish().transform((v) => v ?? undefined),
    /** The agent's previous machine token: re-running the installer keeps that machine and rotates its token (same user and OS, doc 06). */
    replacesToken: z.string().max(128).optional(),
});
export const AgentPairResponseSchema = z.object({
    machineId: z.string(),
    machineToken: z.string(),
    machineName: z.string(),
    serverUrl: z.string(),
});
/** With a recipe, ComfyUI opens with that recipe's workflow loaded (doc 24). */
export const OpenMachineRequestSchema = z.object({
    recipeId: z.string().min(1).max(64).optional(),
    version: z.number().int().min(1).optional(),
});
export const OpenMachineResponseSchema = z.object({ url: z.string() });
export const RestartMachineResponseSchema = z.object({ restarted: z.boolean() });
//# sourceMappingURL=machines.js.map